import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './component/LoginPage';
import './App.css';
import UserCategoryPage from './component/UserCategoryPage'; // Import the new component for user category view
import UserCompanyPage from './component/UserCompanyPage';
import CategoriesPage from './component/CategoriesPage'; // Import the categories page component
import Dashboard from './Dashboard';
import UserLayout from './component/UserLayout';
import ProfilePage from './component/ProfilePage';
import { CartProvider } from './context/CartContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './component/CustomToast.css';
import CartPage from './component/CartPage';
import CheckoutPage from './component/CheckoutPage';
import OrderDetailsPage from './component/OrderDetailsPage';
import { CategoryProvider } from './context/CategoryContext';
import AdminDashboard from './component/AdminDashboard';
import AdminCategoryPage from './component/AdminCategoryPage';
import AdminProductPage from './component/AdminProductPage';
import AddCategoryPage from './component/AddCategoryPage';
import AddProductPage from './component/AddProductPage';
import MyOrdersPage from './component/MyOrdersPage';
import OrderHistoryPage from './component/OrderHistoryPage';
import ScrollToTop from './component/ScrollToTop';

export default function App() {
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        const validUser = normalizeUser(parsed);
        if (validUser) {
          setUser(validUser);
        } else {
          localStorage.removeItem('user');
        }
      } catch {
        localStorage.removeItem('user');
      }
    }
  }, []);

  const roleValue = (user && (user.role || user.Role)) ? String(user.role || user.Role).toLowerCase() : '';
  const isAdmin = !!(user && (roleValue === 'admin' || user.isAdmin === true));

  return (
    <CartProvider>
      <CategoryProvider>
      <Router>
        <ScrollToTop />
        <Routes>
          {isAdmin ? (
            <>
              <Route path="/" element={<AdminDashboard />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/categories" element={<AdminCategoryPage />} />
              <Route path="/admin/products" element={<AdminProductPage />} />
              <Route path="/admin/add-category" element={<AddCategoryPage />} />
              <Route path="/admin/add-product" element={<AddProductPage />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </>
          ) : (
            <>
              <Route
                path="/login"
                element={
                  <div className="main-bg">
                    <nav className="navbar">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img 
                          src={require('./Assets/WebsiteLogo.png')} 
                          alt="Jai Bhavani Logo" 
                          style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                        />
                        <span className="navbar-title">જય ભવાની ફટાકડા સ્ટોર</span>
                      </div>
                    </nav>
                    <div className="login-center">
                      <LoginPage
                        onLogin={(payload: any) => {
                          console.log('Login payload received:', payload); // Debug log
                          const nextUser = normalizeUser(payload);
                          console.log('Normalized user:', nextUser); // Debug log
                          if (!nextUser) {
                            alert('Invalid email or password.');
                            return;
                          }
                          setUser(nextUser);
                          localStorage.setItem('user', JSON.stringify(nextUser));
                          console.log('User set and stored, redirecting...'); // Debug log
                          // Redirect to dashboard after successful login
                          window.location.href = '/';
                        }}
                      />
                    </div>
                  </div>
                }
              />
              <Route
                path="/"
                element={
                  <UserLayout user={user} setUser={setUser}>
                    <Dashboard user={user} setUser={setUser} />
                  </UserLayout>
                }
              />
              <Route
                path="/orders"
                element={
                  <MyOrdersPage user={user} setUser={setUser} />
                }
              />
              <Route
                path="/profile"
                element={
                  <UserLayout user={user} setUser={setUser}>
                    <ProfilePage />
                  </UserLayout>
                }
              />
              <Route
                path="/category"
                element={
                  <UserLayout user={user} setUser={setUser} hideSlider={true}>
                    <CategoriesPage />
                  </UserLayout>
                }
              />
              <Route
                path="/company/:companySlug"
                element={
                  <UserLayout user={user} setUser={setUser} hideSlider={true}>
                    <UserCompanyPage />
                  </UserLayout>
                }
              />
              <Route
                path="/:categoryName"
                element={
                  <UserLayout user={user} setUser={setUser} hideSlider={true}>
                    <UserCategoryPage />
                  </UserLayout>
                }
              />
              <Route path="/cart" element={<CartPage user={user} setUser={setUser} />} />
              <Route path="/checkout" element={<CheckoutPage user={user} setUser={setUser} />} />
              <Route path="/order/:orderId" element={<OrderDetailsPage user={user} setUser={setUser} />} />
              <Route path="/my-orders" element={<OrderHistoryPage user={user} setUser={setUser} />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </Router>
      <ToastContainer
        position="top-center"
        newestOnTop={true}
        closeOnClick={false}
        pauseOnHover={false}
        hideProgressBar={true}
        autoClose={1000}
        theme="light"
        limit={1}
        rtl={false}
        draggable={false}
        closeButton={false}
        className="custom-toast-wrapper"
        style={{ top: '60px' }}
      />
      </CategoryProvider>
    </CartProvider>
  );
}

function normalizeUser(input: any): any | null {
  if (!input) return null;
  // Envelope pattern: { statusCode, value }
  if (typeof input === 'object' && 'statusCode' in input) {
    if (input.statusCode === 200 && input.value) return input.value;
    return null;
  }
  // Direct user object must have at least a user identifier or role
  const hasIdentifier = !!(input.userId || input.UserId || input.id);
  const hasNameOrRole = !!(input.userName || input.name || input.role || input.Role || input.isAdmin);
  if (hasIdentifier || hasNameOrRole) return input;
  return null;
}